import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { currentRouteSet } from "../../actions/actions";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { API_URL } from "../../config";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import SearchIcon from "@material-ui/icons/Search";
import PrintIcon from "@material-ui/icons/Print";
import GetAppIcon from "@material-ui/icons/GetApp";
import InstitutionProfile from "../commons/institution_profile";
import { NetworkLoaderOverlay } from "../commons/NetworkLoader";
import ReactToPrint from "react-to-print";
import commaNumber from "comma-number";
import swal from "sweetalert";
import { exportToExcel, pathSpliter } from "../../lib/functions";
import "../commons/commons.css";

const SEARCH_FIELD_ALIASES = {
  name: ["name", "product", "productname"],
  code: ["code", "sku", "productcode"],
  barcode: ["barcode", "bar", "bar_code"],
  brand: ["brand"],
  category: ["category", "cat"],
  type: ["type", "producttype"],
};

const ProductSearch = ({ location, currentRouteSet, authInfo }) => {
  const classes = useStyles();

  const [products, productsSet] = useState([]);
  const [selectedProduct, selectedProductSet] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  const [stockData, stockDataSet] = useState([]);
  const reportRef = useRef(null);
  const [print, printSet] = useState(false);
  const [reportLoading, reportLoadingSet] = useState(false);

  // Put the ref on the TextField input (NOT on Autocomplete)
  const productInputRef = useRef(null);

  let format = commaNumber.bindWith(",", ".");
  const cleanProductName = (name) =>
    String(name || "")
      .replace(/^\s*\d+\s+/, "")
      .trim();
  const normalizeToken = (text) =>
    String(text || "")
      .trim()
      .toLowerCase()
      .replace(/[–—−]/g, "-");
  const getProductLabel = (option) => {
    const parts = [
      cleanProductName(option?.prod_name),
      String(option?.prod_brand_name || "").trim(),
      String(option?.prod_color_name || "").trim(),
    ].filter(Boolean);
    return parts.join(" - ");
  };
  const normalizeText = (text) => normalizeToken(text);
  const getProductBarcode = (product) =>
    String(
      product?.prod_barcode ??
      product?.barcode ??
      product?.bar_code ??
      product?.product_barcode ??
      ""
    ).trim();
  const getProductType = (product) =>
    String(
      product?.prod_type_name ??
      product?.prod_type ??
      product?.product_type ??
      product?.type ??
      (product?.prod_is_service === "true" || product?.prod_is_service === true ? "service" : "finished")
    ).trim();
  const getProductSearchParts = (product) => [
    cleanProductName(product?.prod_name),
    product?.prod_name,
    product?.prod_code,
    getProductBarcode(product),
    product?.prod_cat_name,
    product?.prod_brand_name,
    product?.prod_color_name,
    getProductType(product),
  ].filter(Boolean);
  const getProductSearchText = (product) =>
    normalizeText(getProductSearchParts(product).join(" "));
  const parseAdvancedQuery = (queryText) => {
    const raw = String(queryText || "").trim();
    const fieldMatches = [...raw.matchAll(/([a-zA-Z]+):([^\s]+)/g)];
    const fieldFilters = fieldMatches.reduce((acc, [, rawKey, rawValue]) => {
      const canonicalKey = Object.entries(SEARCH_FIELD_ALIASES).find(([, aliases]) =>
        aliases.includes(normalizeText(rawKey))
      )?.[0];
      if (canonicalKey) {
        acc[canonicalKey] = normalizeText(rawValue);
      }
      return acc;
    }, {});

    const freeText = raw.replace(/([a-zA-Z]+):([^\s]+)/g, " ").trim();
    const tokens = freeText
      .split(/\s+/)
      .map(normalizeText)
      .filter(Boolean);

    return { fieldFilters, tokens, raw: normalizeText(raw) };
  };
  const productMatchesQuery = (product, queryText) => {
    const { fieldFilters, tokens, raw } = parseAdvancedQuery(queryText);
    if (!raw) return true;

    const fieldValues = {
      name: normalizeText(cleanProductName(product?.prod_name) || product?.prod_name),
      code: normalizeText(product?.prod_code),
      barcode: normalizeText(getProductBarcode(product)),
      brand: normalizeText(product?.prod_brand_name),
      category: normalizeText(product?.prod_cat_name),
      type: normalizeText(getProductType(product)),
    };

    const fieldFilterOk = Object.entries(fieldFilters).every(([key, value]) =>
      fieldValues[key]?.includes(value)
    );
    if (!fieldFilterOk) return false;

    const haystack = getProductSearchText(product);
    if (tokens.length === 0) return true;
    return tokens.every((token) => haystack.includes(token));
  };
  const getResolvedProductId = (product) => {
    const rawId =
      product?.prod_id ??
      product?.product_id ??
      product?.id ??
      product?.prodId ??
      (typeof product?.value === "object"
        ? product?.value?.prod_id ?? product?.value?.product_id ?? product?.value?.id
        : product?.value);
    return rawId === undefined || rawId === null || rawId === "" ? null : rawId;
  };
  const findProductByTypedText = (text) => {
    const query = normalizeText(text);
    if (!query) return null;

    const exact = products.find((p) => normalizeText(getProductLabel(p)) === query);
    if (exact) return exact;

    const exactByCode = products.find((p) => normalizeText(p?.prod_code) === query);
    if (exactByCode) return exactByCode;

    const exactByBarcode = products.find((p) => normalizeText(getProductBarcode(p)) === query);
    if (exactByBarcode) return exactByBarcode;

    const exactByName = products.find((p) => normalizeText(cleanProductName(p?.prod_name)) === query);
    if (exactByName) return exactByName;

    return (
      products.find((p) => productMatchesQuery(p, text)) || null
    );
  };
  const resolveProductForSearch = (candidateProduct, typedText = "") => {
    if (candidateProduct) {
      if (getResolvedProductId(candidateProduct) !== null) return candidateProduct;
      const byCandidateLabel = findProductByTypedText(
        typeof candidateProduct === "string" ? candidateProduct : getProductLabel(candidateProduct)
      );
      if (byCandidateLabel) return byCandidateLabel;
    }
    return findProductByTypedText(typedText);
  };

  useEffect(() => {
    currentRouteSet(pathSpliter(location.pathname, 1));
    getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProducts = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/get-products`,
        { "select-type": "active" },
        { headers: { "auth-token": authInfo.token } }
      );

      // Make sure it's always an array
      productsSet(Array.isArray(res?.data?.message) ? res.data.message : []);
    } catch (err) {
      productsSet([]);
      swal({ title: "Failed to load products", text: err?.message, icon: "error" });
    }
  };

  const handleKeyDown = (e) => {
    if (!stockData || stockData.length === 0) return;

    if (e.key === "ArrowDown") {
      setSelectedRow((prev) =>
        prev === null || prev === stockData.length - 1 ? 0 : prev + 1
      );
    }
    if (e.key === "ArrowUp") {
      setSelectedRow((prev) =>
        prev === null || prev === 0 ? stockData.length - 1 : prev - 1
      );
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [stockData]);

  const getSearchResult = async (productOverride = null) => {
    const typedText = searchInput || productInputRef.current?.value || "";
    const productToSearch = resolveProductForSearch(productOverride || selectedProduct, typedText);
    const resolvedProductId = getResolvedProductId(productToSearch);
    const normalizedTypedText = String(typedText || "").trim();

    if (resolvedProductId === null && !normalizedTypedText) {
      swal({ title: "Product not found", text: "Try name, code, barcode, or multiple keywords.", icon: "warning" });
      return;
    }
    if (productToSearch && productToSearch !== selectedProduct) {
      selectedProductSet(productToSearch);
    }

    const payload = resolvedProductId !== null
      ? { productId: resolvedProductId, searchText: normalizedTypedText }
      : { searchText: normalizedTypedText };

    reportLoadingSet(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/get-product-location`,
        payload,
        { headers: { "auth-token": authInfo.token } }
      );

      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const grouped = new Map();

      data.forEach((row) => {
        const purchaseRate = parseFloat(row?.prod_purchase_rate ?? 0);
        const key = [
          row?.prod_id,
          row?.prod_floor_name ?? "",
          row?.prod_rack_name ?? "",
          purchaseRate.toFixed(4),
        ].join("||");

        if (!grouped.has(key)) {
          grouped.set(key, {
            ...row,
            prod_qty: parseFloat(row?.prod_qty ?? 0),
          });
          return;
        }

        const existing = grouped.get(key);
        grouped.set(key, {
          ...existing,
          prod_qty: parseFloat(existing?.prod_qty ?? 0) + parseFloat(row?.prod_qty ?? 0),
        });
      });

      stockDataSet(Array.from(grouped.values()));
      setSelectedRow(null);
      printSet(true);
    } catch (err) {
      stockDataSet([]);
      printSet(false);
      swal({ title: "Search failed", text: err?.message, icon: "error" });
    } finally {
      reportLoadingSet(false);
      // focus back to input
      if (productInputRef.current) productInputRef.current.focus();
    }
  };

  const handleExcelExport = () => {
    if (!stockData || stockData.length === 0) return;

    const exportData = stockData.map((row, sl) => ({
      SL: sl + 1,
      Floor: row?.prod_floor_name ?? "",
      Rack: row?.prod_rack_name ?? "",
      Category: row?.prod_cat_name ?? "",
      Brand: row?.prod_brand_name ?? "",
      "Product Name": row?.prod_name ?? "",
      "Current Quantity": `${format(parseFloat(row?.prod_qty ?? 0).toFixed(2))} ${row?.prod_unit_name ?? ""}`,
      "Purchase Rate": format(parseFloat(row?.prod_purchase_rate ?? 0).toFixed(2)),
      "Average Purchase Rate": format(parseFloat(row?.average_purchase_rate ?? 0).toFixed(2)),
      "Last Sale Rate": format(parseFloat(row?.last_sale_rate ?? 0).toFixed(2)),
      "Last Sale Date": row?.last_sale_date ? row.last_sale_date.split("T")[0] : "",
      "Product Code": row?.prod_code ?? "",
    }));

    const columnWidths = [
      { wch: 5 },
      { wch: 15 },
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 18 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 25 },
    ];

    const name =
      selectedProduct?.prod_name ||
      selectedProduct?.product_name ||
      "Product";

    exportToExcel(exportData, `${name} storage report.xlsx`, columnWidths);
  };

  const ReportDom = React.forwardRef((props, ref) => {
    const name =
      selectedProduct?.prod_name ||
      selectedProduct?.product_name ||
      "";

    return (
      <div ref={ref}>
        <InstitutionProfile />

        <div style={{ clear: "both" }} />

        {name ? (
          <Paper
            style={{
              width: "100%",
              paddingLeft: "10px",
              paddingRight: "10px",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
            className="print-source"
          >
            <p style={{ width: "100%", textAlign: "center", fontWeight: 700, letterSpacing: "0.2px" }}>
              {`${name} storage report`}
            </p>
          </Paper>
        ) : null}

        {Array.isArray(stockData) && stockData.length > 0 ? (
          <Paper className={`${classes.paper} ${classes.tableCard}`} style={{ marginTop: "10px", marginBottom: "5px" }}>
            <div className={classes.tableHeaderBar}>
              <div>
                <div className={classes.tableTitle}>Search Results</div>
                <div className={classes.tableSubtitle}>{stockData.length} product{stockData.length > 1 ? "s" : ""} found</div>
              </div>
            </div>
            <TableContainer className={classes.tableContainer}>
              <Table className={`${classes.table} report-dom`} size="small">
                <TableHead>
                  <TableRow className={classes.tableHeadRow}>
                    <TableCell align="center" className={classes.headCell} style={{ width: "4%" }}>SL</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "10%" }}>Floor</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "8%" }}>Rack</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "10%" }}>Category</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "10%" }}>Brand</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "12%" }}>Product Name</TableCell>
                    <TableCell align="right" className={classes.headCell} style={{ width: "9%" }}>Current Quantity</TableCell>
                    <TableCell align="right" className={classes.headCell} style={{ width: "8%" }}>Purchase Rate</TableCell>
                    <TableCell align="right" className={classes.headCell} style={{ width: "12%" }}>Purchase Average Rate</TableCell>
                    <TableCell align="right" className={classes.headCell} style={{ width: "8%" }}>Last Sale Rate</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "10%" }}>Last Sale Date</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "8%" }}>Product Code</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stockData.map((row, sl) => (
                    <TableRow
                      key={sl}
                      onClick={() => setSelectedRow(sl)}
                      style={{
                        backgroundColor:
                          selectedRow === sl
                            ? "#e9f7fb"
                            : sl % 2 === 0
                              ? "#ffffff"
                              : "#fbfdff",
                        cursor: "pointer",
                      }}
                    >
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{sl + 1}.</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{row?.prod_floor_name ?? ""}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{row?.prod_rack_name ?? ""}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{row?.prod_cat_name ?? ""}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{row?.prod_brand_name ?? ""}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row?.prod_name ?? ""}
                      </TableCell>
                      <TableCell align="right" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {format(parseFloat(row?.prod_qty ?? 0).toFixed(2))}{" "}
                        {row?.prod_unit_name ?? ""}
                      </TableCell>
                      <TableCell align="right" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {format(parseFloat(row?.prod_purchase_rate ?? 0).toFixed(2))}
                      </TableCell>
                      <TableCell align="right" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {format(parseFloat(row?.average_purchase_rate ?? 0).toFixed(2))}
                      </TableCell>
                      <TableCell align="right" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {format(parseFloat(row?.last_sale_rate ?? 0).toFixed(2))}
                      </TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {row?.last_sale_date ? row.last_sale_date.split("T")[0] : ""}
                      </TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{row?.prod_code ?? ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : null}
      </div>
    );
  });

  return (
    <div className={classes.root}>
      <NetworkLoaderOverlay visible={reportLoading} message="Loading report..." />
      <Paper className={classes.paper} style={{ marginTop: "-25px", marginBottom: "5px" }}>
        <div className={classes.searchHero}>
          <div>
            <h4 className={classes.pageTitle}>Search Product</h4>
            <div className={classes.pageSubtitle}>Search by name, code, barcode, or brand</div>
          </div>
          <div className={classes.actionIcons}>
            <div className={classes.iconBtn} aria-hidden="true">PRN</div>
            <div className={classes.iconBtn} aria-hidden="true">DL</div>
          </div>
        </div>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <Autocomplete
              size="small"
              autoHighlight
              openOnFocus
              freeSolo
              style={{ width: "100%", height: "20px" }}
              options={products}
              value={selectedProduct}
              inputValue={searchInput}
              getOptionLabel={getProductLabel}
              filterOptions={(options, state) =>
                options.filter((option) => productMatchesQuery(option, state.inputValue))
              }
              onInputChange={(event, newInputValue, reason) => {
                setSearchInput(newInputValue || "");
                if (reason === "clear") {
                  selectedProductSet(null);
                  stockDataSet([]);
                  printSet(false);
                  setSelectedRow(null);
                }
              }}

              onChange={(event, selectedObj) => {
                selectedProductSet(selectedObj);
                setSearchInput(selectedObj ? getProductLabel(selectedObj) : "");
                stockDataSet([]);
                printSet(false);
                setSelectedRow(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const isDropdownOpen = e?.target?.getAttribute("aria-expanded") === "true";
                  if (isDropdownOpen) {
                    // keep default Autocomplete enter behavior (select highlighted option)
                    return;
                  }
                  e.preventDefault();
                  if (getResolvedProductId(selectedProduct) === null) {
                    const matchedProduct = findProductByTypedText(e?.target?.value);
                    if (getResolvedProductId(matchedProduct) !== null) {
                      selectedProductSet(matchedProduct);
                      getSearchResult(matchedProduct);
                      return;
                    }
                  }
                  getSearchResult();
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by name, code, barcode, brand..."
                  placeholder="Laptop Dell | code:LP1001 | barcode:890123..."
                  variant="outlined"
                  inputRef={productInputRef}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={2} md={1}>
            <Button
              style={{ marginTop: "5px", marginLeft: "auto", fontSize: "14px", minWidth: "120px" }}
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={getSearchResult}
              disabled={reportLoading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {print ? (
        <Grid container>
          <Grid item xs={12}>
            <Paper style={{ borderRadius: "0px", marginTop: "-7px" }}>
              <ReactToPrint
                trigger={() => (
                  <PrintIcon
                    style={{
                      cursor: "pointer",
                      marginLeft: "30px",
                      marginTop: "3px",
                      marginBottom: "-8px",
                    }}
                  />
                )}
                content={() => reportRef.current}
              />
              <Tooltip title="Excel Download">
                <IconButton onClick={handleExcelExport}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      <ReportDom ref={reportRef} />
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    background: "linear-gradient(180deg, #f7fbfc 0%, #ffffff 100%)",
    minHeight: "100%",
  },
  table: {
    width: "100%",
    tableLayout: "fixed",
  },
  paper: {
    padding: theme.spacing(1.25),
    textAlign: "left",
    color: theme.palette.text.secondary,
    borderRadius: "12px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  tableCard: {
    border: "1px solid rgba(0,0,0,0.06)",
  },
  tableContainer: {
    borderRadius: "10px",
    overflowX: "auto",
  },
  tableHeaderBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
    padding: "2px 2px 10px 2px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },
  tableTitle: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#263238",
  },
  tableSubtitle: {
    fontSize: "12px",
    color: "#607d8b",
    marginTop: "2px",
  },
  tableHeadRow: {
    background: "linear-gradient(180deg, #f8fbfc 0%, #eef5f8 100%)",
  },
  headCell: {
    fontWeight: 700,
    fontSize: "12px",
    color: "#37474f",
    paddingTop: "9px",
    paddingBottom: "9px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    fontSize: "12.5px",
    color: "#263238",
    paddingTop: "8px",
    paddingBottom: "8px",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
  },
  searchHero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "-10px",
    marginBottom: "8px",
  },
  pageTitle: {
    textAlign: "left",
    margin: 0,
    padding: 0,
    fontSize: "16px",
    fontWeight: 700,
    color: "#1f2937",
  },
  pageSubtitle: {
    fontSize: "12px",
    color: "#607d8b",
    marginTop: "2px",
  },
  actionIcons: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "#546e7a",
  },
  iconBtn: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f4f8fa",
    border: "1px solid rgba(0,0,0,0.06)",
    fontSize: "14px",
    cursor: "default",
    userSelect: "none",
  },
}));

const mapStateToPops = (state) => ({
  currentRoute: state.currentRouteReducer,
  authInfo: state.authInfoReducer,
});

export default connect(mapStateToPops, { currentRouteSet })(ProductSearch);
